/**
 * @name ModalRecarga
 * @author Gabriel M. Guimarães | gabrielmg7
 * @description Modal para recarga de créditos via PIX com confirmação
 * @package components/pages/gestao-financeira/creditos/components
 */

import { Close as CloseIcon, OpenInNew as OpenInNewIcon, Pix as PixIcon } from '@mui/icons-material';
import { Alert, AlertTitle, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputAdornment, InputLabel, OutlinedInput, Stack, TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../../../../supabaseConfig';
import useUserContext from '../../../../../contexts/UserContext/useUserContext';
import { useSolicitacaoCredito } from '../../../../../hooks/useSolicitacaoCredito';
import ModalCadastroDados from './ModalCadastroDados';
import ModalConfirmacaoPix from './ModalConfirmacaoPix';

interface ModalRecargaProps {
     open: boolean;
     onClose: () => void;
     onSuccess?: () => void;
     valorInicial?: number;
     descricaoInicial?: string;
}

/**
 * Modal para recarga de créditos via PIX
 * Sempre usa PIX como método de pagamento
 */
const ModalRecarga: React.FC<ModalRecargaProps> = ({ open, onClose, onSuccess, valorInicial }) => {
     // Estados do formulário
     const [valor, setValor] = useState<string>('');
     // Descrição não será mais controlada por input
     const [descricao, setDescricao] = useState<string>('');
     const [showConfirmacao, setShowConfirmacao] = useState(false);
     const [popupBloqueado, setPopupBloqueado] = useState(false);
     const [urlPagamentoPendente, setUrlPagamentoPendente] = useState<string | null>(null);

     const { userData } = useUserContext();
     const { loading, error, modalCadastral, criarSolicitacao, fecharModalCadastral, salvarDadosCadastrais, clearError } = useSolicitacaoCredito(userData?.fk_id_organizacao || 0);

     const [orgNome, setOrgNome] = useState('');
     const [orgCnpj, setOrgCnpj] = useState('');

     useEffect(() => {
          const fetchOrganizacao = async () => {
               if (!userData?.fk_id_organizacao) return;
               const { data } = await supabase.from('organizacoes').select('nome, cnpj').eq('id', userData.fk_id_organizacao).single();
               if (data) {
                    setOrgNome(data?.nome!);
                    setOrgCnpj(data?.cnpj!);
               }
          };
          fetchOrganizacao();
     }, [userData?.fk_id_organizacao]);

     // Efeito para definir valores iniciais e descrição automática
     React.useEffect(() => {
          if (open) {
               setValor(valorInicial ? valorInicial.toString() : '');
               // Descrição automática com dados da organização
               setDescricao(`Recarga de Crédito - ${orgNome}${orgCnpj ? ' - ' + orgCnpj : ''} - Para uso exclusivo da Cantina.`);
          }
     }, [open, valorInicial, orgNome, orgCnpj]);

     // Validações
     const valorNumerico = parseFloat(valor) || 0;
     const isValorValido = valorNumerico >= 1 && valorNumerico <= 1000;
     // Descrição é sempre preenchida automaticamente
     const isFormularioValido = isValorValido;

     // Limpar formulário
     const limparFormulario = useCallback(() => {
          setValor('');
          setDescricao('');
          setShowConfirmacao(false);
          setPopupBloqueado(false);
          setUrlPagamentoPendente(null);
          clearError();
     }, [clearError]);

     // Fechar modal
     const handleClose = useCallback(() => {
          if (!loading) {
               limparFormulario();
               onClose();
          }
     }, [loading, limparFormulario, onClose]);

     // Mostrar confirmação
     const handleShowConfirmacao = useCallback(() => {
          if (!isFormularioValido) return;
          setShowConfirmacao(true);
     }, [isFormularioValido]);

     // Processar recarga (após confirmação)
     const handleConfirmarRecarga = useCallback(async () => {
          try {
               clearError();

               const urlSolicitacao = await criarSolicitacao({
                    valor_solicitacao: valorNumerico,
                    descricao_solicitacao: descricao.trim(),
                    timestamp_limite_solicitacao: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
               });

               if (urlSolicitacao) {
                    // Fechar modal de confirmação e notificar sucesso
                    setShowConfirmacao(false);
                    onSuccess?.();

                    // Pequeno delay para feedback visual antes de abrir a janela
                    setTimeout(() => {
                         const novaJanela = window.open(urlSolicitacao, '_blank', 'noopener,noreferrer');
                         if (!novaJanela || novaJanela.closed || typeof novaJanela.closed === 'undefined') {
                              // Popup bloqueado pelo navegador — manter modal aberto com aviso
                              setPopupBloqueado(true);
                              setUrlPagamentoPendente(urlSolicitacao);
                         } else {
                              handleClose();
                         }
                    }, 500);
               }
          } catch (error) {
               console.error('Erro ao processar recarga:', error);
               setShowConfirmacao(false);
          }
     }, [valorNumerico, descricao, criarSolicitacao, handleClose, onSuccess, clearError]);

     // Valores sugeridos (removido valores rápidos conforme solicitado)
     const valoresSugeridos = [1, 5, 10, 20, 50, 100];

     return (
          <>
               <Dialog
                    open={open && !showConfirmacao}
                    onClose={handleClose}
                    maxWidth='sm'
                    fullWidth
                    PaperProps={{ sx: { borderRadius: 2 } }}
               >
                    <DialogTitle sx={{ m: 0, p: 2 }}>
                         <Typography
                              variant='h6'
                              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                         >
                              <PixIcon color='primary' />
                              Recarregar via PIX
                         </Typography>
                         <Typography
                              variant='body2'
                              color='text.secondary'
                              sx={{ mt: 0.5 }}
                         >
                              Adicione créditos à sua conta usando PIX instantâneo
                         </Typography>
                         <IconButton
                              aria-label='close'
                              onClick={handleClose}
                              disabled={loading}
                              sx={{
                                   position: 'absolute',
                                   right: 8,
                                   top: 8,
                                   color: (theme) => theme.palette.grey[500],
                              }}
                         >
                              <CloseIcon />
                         </IconButton>
                    </DialogTitle>

                    <DialogContent sx={{ pt: 2 }}>
                         <Stack spacing={3}>
                              {/* Alerta de popup bloqueado */}
                              {popupBloqueado && urlPagamentoPendente && (
                                   <Alert
                                        severity='warning'
                                        onClose={() => { setPopupBloqueado(false); setUrlPagamentoPendente(null); }}
                                   >
                                        <AlertTitle>Navegador bloqueou o redirecionamento</AlertTitle>
                                        Seu navegador bloqueou a abertura da página de pagamento PIX. Permita pop-ups para este site ou clique no botão abaixo para acessar manualmente.
                                        <Box sx={{ mt: 1.5 }}>
                                             <Button
                                                  variant='contained'
                                                  color='warning'
                                                  size='small'
                                                  endIcon={<OpenInNewIcon />}
                                                  href={urlPagamentoPendente}
                                                  target='_blank'
                                                  rel='noopener noreferrer'
                                                  onClick={handleClose}
                                             >
                                                  Abrir página de pagamento
                                             </Button>
                                        </Box>
                                   </Alert>
                              )}

                              {/* Alerta de erro */}
                              {error && (
                                   <Alert
                                        severity='error'
                                        onClose={clearError}
                                   >
                                        {error}
                                   </Alert>
                              )}

                              {/* Valor da Recarga */}
                              <FormControl fullWidth>
                                   <InputLabel htmlFor='valor-recarga'>Valor da Recarga</InputLabel>
                                   <OutlinedInput
                                        id='valor-recarga'
                                        value={valor}
                                        onChange={(e) => setValor(e.target.value)}
                                        startAdornment={<InputAdornment position='start'>R$</InputAdornment>}
                                        label='Valor da Recarga'
                                        type='number'
                                        inputProps={{
                                             min: '1.00',
                                             max: '1000.00',
                                             step: '0.01',
                                        }}
                                        disabled={loading}
                                        error={valor !== '' && !isValorValido}
                                   />
                                   <Typography
                                        variant='caption'
                                        color={valor !== '' && !isValorValido ? 'error' : 'text.secondary'}
                                        sx={{ mt: 0.5 }}
                                   >
                                        {valor !== '' && !isValorValido ? (valorNumerico < 1 ? 'Valor mínimo: R$ 1,00' : 'Valor máximo: R$ 1.000,00') : 'Valor mínimo: R$ 1,00 | Valor máximo: R$ 1.000,00'}
                                   </Typography>
                              </FormControl>

                              {/* Valores Sugeridos */}
                              <div>
                                   <Typography
                                        variant='caption'
                                        color='text.secondary'
                                        sx={{ mb: 1, display: 'block' }}
                                   >
                                        Valores sugeridos:
                                   </Typography>
                                   <Stack
                                        direction='row'
                                        spacing={1}
                                        flexWrap='wrap'
                                        useFlexGap
                                   >
                                        {valoresSugeridos.map((valorSugerido) => (
                                             <Button
                                                  key={valorSugerido}
                                                  variant='outlined'
                                                  size='small'
                                                  onClick={() => setValor(valorSugerido.toString())}
                                                  disabled={loading}
                                             >
                                                  R$ {valorSugerido}
                                             </Button>
                                        ))}
                                   </Stack>
                              </div>

                              {/* Descrição automática */}
                              <TextField
                                   label='Descrição da Recarga'
                                   value={descricao}
                                   disabled
                                   helperText='Descrição gerada automaticamente com nome e CNPJ da escola.'
                                   inputProps={{ maxLength: 100 }}
                              />

                              {/* Informação sobre PIX */}
                              <Alert severity='info'>
                                   <Typography variant='body2'>
                                        <strong>🔒 Pagamento 100% via PIX</strong>
                                        <br />
                                        Transação instantânea e segura através do Banco do Brasil
                                   </Typography>
                              </Alert>

                              {/* Resumo (se válido) */}
                              {isFormularioValido && (
                                   <Box
                                        sx={{
                                             p: 2,
                                             bgcolor: 'success.50',
                                             borderRadius: 1,
                                             border: '1px solid',
                                             borderColor: 'success.200',
                                        }}
                                   >
                                        <Typography
                                             variant='body2'
                                             color='success.main'
                                             fontWeight='medium'
                                        >
                                             Resumo da Recarga
                                        </Typography>
                                        <Typography
                                             variant='h6'
                                             color='success.main'
                                             sx={{ mt: 0.5 }}
                                        >
                                             R$ {valorNumerico.toFixed(2)}
                                        </Typography>
                                        <Typography
                                             variant='body2'
                                             sx={{ mt: 0.5 }}
                                        >
                                             {descricao.trim()}
                                        </Typography>
                                   </Box>
                              )}
                         </Stack>
                    </DialogContent>

                    <DialogActions sx={{ px: 3, pb: 2 }}>
                         <Button
                              onClick={handleClose}
                              disabled={loading}
                         >
                              Cancelar
                         </Button>
                         <Button
                              onClick={handleShowConfirmacao}
                              variant='contained'
                              disabled={!isFormularioValido || loading}
                              startIcon={<PixIcon />}
                         >
                              Gerar PIX de R$ {valorNumerico.toFixed(2)}
                         </Button>
                    </DialogActions>
               </Dialog>

               {/* Modal de Confirmação */}
               <ModalConfirmacaoPix
                    open={showConfirmacao}
                    onClose={() => setShowConfirmacao(false)}
                    onConfirm={handleConfirmarRecarga}
                    valor={valorNumerico}
                    descricao={descricao.trim()}
                    loading={loading}
               />

               {/* Modal de Dados Cadastrais */}
               <ModalCadastroDados
                    open={modalCadastral.precisaModal}
                    onClose={fecharModalCadastral}
                    onSave={salvarDadosCadastrais}
                    camposFaltantes={modalCadastral.camposFaltantes}
                    loading={loading}
               />
          </>
     );
};

export default ModalRecarga;
